import React, { useEffect, useRef } from "react";
import { FunnelCustomFieldProps } from "components/form-funnel/types";
import DatePickerx from "features/datepicker/date-picker";
import { defaultSalaryChangeDate } from "./salary-change-helpers";

/**
 * `salaryChangeDate` as a FormFunnel `custom` field (docs/prd/probation-salary-review.md
 * §7.1): a plain date picker that additionally prefills itself with
 * `addMonthsClamped(hiringDate, probationMonths)` (the day after probation
 * ends) the moment it becomes visible with nothing in it yet. `visibleWhen`
 * unmounts this field while `hasSalaryChange` is off, so "becomes visible"
 * and "mounts" are the same event — a plain mount effect is enough, no need
 * to watch the checkbox itself. When hiringDate/probationMonths aren't known
 * yet, the field just stays empty for the editor to fill in.
 */
export default function SalaryChangeDateField({ value, onChange, label, required, error, readOnly, data }: FunnelCustomFieldProps) {
  const didPrefill = useRef(false);

  useEffect(() => {
    if (didPrefill.current) return;
    didPrefill.current = true;
    if (value != null) return;
    const hiringDate = typeof data?.hiringDate === "number" ? data.hiringDate : null;
    const probationMonths = typeof data?.probationMonths === "number" ? data.probationMonths : null;
    if (hiringDate == null || probationMonths == null || probationMonths <= 0) return;
    onChange(defaultSalaryChangeDate(hiringDate, probationMonths));
    // Intentionally mount-only: once prefilled, the editor's own edits (incl.
    // clearing the field) must never be overwritten again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DatePickerx
      title={label}
      selected={value ? new Date(value).toISOString().split("T")[0] : ""}
      onChange={(date: Date | null) => onChange(date ? date.getTime() : null)}
      isRequired={required}
      disableDatesAfter={null}
      readOnly={readOnly}
    />
  );
}
